import { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";

const NotificationContext = createContext(null);

export function NotificationProvider({ children }) {
  const [notifications, setNotifications] = useState([]);
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);

  const unreadCount = notifications.filter((n) => !n.read).length;

  // WebSocket connection
  useEffect(() => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
    const wsUrl = backendUrl
      .replace("https://", "wss://")
      .replace("http://", "ws://");

    const connect = () => {
      try {
        const ws = new WebSocket(`${wsUrl}/api/ws/notifications`);
        wsRef.current = ws;

        ws.onmessage = (event) => {
          try {
            const notification = JSON.parse(event.data);
            setNotifications((prev) => [notification, ...prev].slice(0, 100));
            // No toast popups - notifications only shown on Notifications page
          } catch {
            // ignore parse errors
          }
        };

        ws.onclose = () => {
          reconnectTimer.current = setTimeout(connect, 5000);
        };

        ws.onerror = () => {
          ws.close();
        };
      } catch {
        reconnectTimer.current = setTimeout(connect, 5000);
      }
    };

    connect();

    return () => {
      if (wsRef.current) wsRef.current.close();
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
    };
  }, []);

  const markRead = useCallback((id) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
  }, []);

  const markAllRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  }, []);

  const addNotification = useCallback((notification) => {
    const newNotif = {
      id: Date.now().toString(),
      timestamp: new Date().toISOString(),
      read: false,
      ...notification,
    };
    setNotifications((prev) => [newNotif, ...prev].slice(0, 100));
  }, []);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        markRead,
        markAllRead,
        addNotification,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotifications must be used within NotificationProvider");
  }
  return context;
}
